/**
 * OffertNormalisierenDialog — the E11 broker completion form (P2 Placement Slice 10). Prefilled from
 * item.normalized; «Normalisiert bestaetigen» stays disabled until Praemie/Jahr + Versicherungssumme
 * are set; the submit emits ONLY the changed fields.
 */

import { useMemo, useState } from 'react';
import { Dialog } from '../administration/components';
import { Button } from '../../components/ui/Button';
import { OFFER_DECKUNG_STATUS_VALUES, OFFER_DECKUNG_STATUS_LABELS, fehlendeKernwerte } from '@brokly/shared';
import type { OfferDeckungStatus, OfferEintrag, OfferNormalisiertBody } from '@brokly/shared';

function numStr(v: number | null | undefined): string {
  return v === null || v === undefined ? '' : String(v);
}
function parseNum(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function OffertNormalisierenDialog({
  open,
  onClose,
  item,
  onSubmit,
  saving,
  error,
}: {
  open: boolean;
  onClose: () => void;
  item: OfferEintrag | null;
  onSubmit: (body: OfferNormalisiertBody) => void;
  saving: boolean;
  error: string | null;
}) {
  const n = item?.normalized ?? null;
  const init = useMemo(
    () => ({
      praemie_jahr: numStr(n?.praemie.jahr),
      praemie_netto: numStr(n?.praemie.netto),
      praemie_brutto: numStr(n?.praemie.brutto),
      versicherungssumme: numStr(n?.versicherungssumme),
      selbstbehalt: numStr(n?.selbstbehalt),
      vertragsdauer_monate: numStr(n?.vertragsdauer_monate),
      beginn: n?.beginn ?? '',
      gueltig_bis: n?.gueltig_bis ?? '',
      offert_nummer: n?.offert_nummer ?? '',
      ausschluesse: (n?.ausschluesse ?? []).join('\n'),
    }),
    [n],
  );
  const [f, setF] = useState(init);
  const [deckungen, setDeckungen] = useState(() =>
    (n?.deckungen ?? [])
      .filter((d) => d.rule_key !== null)
      .map((d) => ({ rule_key: d.rule_key as string, label: d.label, status: d.status, deckungssumme: numStr(d.deckungssumme), selbstbehalt: numStr(d.selbstbehalt), hinweis: d.hinweis ?? '' })),
  );

  if (!item) return null;

  const kernwerteOk = parseNum(f.praemie_jahr) !== null && parseNum(f.versicherungssumme) !== null;
  const missing = n ? fehlendeKernwerte(n) : [];

  const submit = () => {
    const body: OfferNormalisiertBody = {};
    if (f.praemie_jahr !== init.praemie_jahr) body.praemie_jahr = parseNum(f.praemie_jahr);
    if (f.praemie_netto !== init.praemie_netto) body.praemie_netto = parseNum(f.praemie_netto);
    if (f.praemie_brutto !== init.praemie_brutto) body.praemie_brutto = parseNum(f.praemie_brutto);
    if (f.versicherungssumme !== init.versicherungssumme) body.versicherungssumme = parseNum(f.versicherungssumme);
    if (f.selbstbehalt !== init.selbstbehalt) body.selbstbehalt = parseNum(f.selbstbehalt);
    if (f.vertragsdauer_monate !== init.vertragsdauer_monate) body.vertragsdauer_monate = parseNum(f.vertragsdauer_monate);
    if (f.beginn !== init.beginn) body.beginn = f.beginn || null;
    if (f.gueltig_bis !== init.gueltig_bis) body.gueltig_bis = f.gueltig_bis || null;
    if (f.offert_nummer !== init.offert_nummer) body.offert_nummer = f.offert_nummer || null;
    if (f.ausschluesse !== init.ausschluesse) {
      body.ausschluesse = f.ausschluesse.split('\n').map((s) => s.trim()).filter((s) => s.length > 0);
    }
    const changedDeckungen = deckungen.filter((d) => {
      const orig = (n?.deckungen ?? []).find((o) => o.rule_key === d.rule_key);
      return !orig || orig.status !== d.status || numStr(orig.deckungssumme) !== d.deckungssumme || numStr(orig.selbstbehalt) !== d.selbstbehalt || (orig.hinweis ?? '') !== d.hinweis;
    });
    if (changedDeckungen.length > 0) {
      body.deckungen = changedDeckungen.map((d) => ({ rule_key: d.rule_key, status: d.status, deckungssumme: parseNum(d.deckungssumme), selbstbehalt: parseNum(d.selbstbehalt), hinweis: d.hinweis || null }));
    }
    onSubmit(body);
  };

  const field = (key: keyof typeof f, label: string, type: 'number' | 'text', testid: string) => (
    <label className="flex flex-col gap-1 text-sm">
      <span className={`text-muted-foreground ${missing.includes(key as string) ? 'text-status-danger' : ''}`}>{label}</span>
      <input
        type={type}
        className="rounded-md border border-border bg-background p-2 text-sm"
        value={f[key]}
        onChange={(e) => setF((prev) => ({ ...prev, [key]: e.target.value }))}
        data-testid={testid}
      />
    </label>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      wide
      title="Offerte normalisieren"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button disabled={!kernwerteOk || saving} onClick={submit} data-testid="offert-normalisieren-submit">
            {saving ? 'Wird gespeichert …' : 'Normalisiert bestaetigen'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3" data-testid="offert-normalisieren-dialog">
        <div className="grid grid-cols-2 gap-3">
          {field('praemie_jahr', 'Praemie/Jahr', 'number', 'offert-norm-praemie-jahr')}
          {field('versicherungssumme', 'Versicherungssumme', 'number', 'offert-norm-vs')}
          {field('praemie_netto', 'Praemie netto', 'number', 'offert-norm-netto')}
          {field('praemie_brutto', 'Praemie brutto', 'number', 'offert-norm-brutto')}
          {field('selbstbehalt', 'Selbstbehalt', 'number', 'offert-norm-sb')}
          {field('vertragsdauer_monate', 'Vertragsdauer (Monate)', 'number', 'offert-norm-dauer')}
          {field('beginn', 'Beginn (YYYY-MM-DD)', 'text', 'offert-norm-beginn')}
          {field('gueltig_bis', 'Gueltig bis (YYYY-MM-DD)', 'text', 'offert-norm-gueltig')}
          {field('offert_nummer', 'Offert-Nummer', 'text', 'offert-norm-nummer')}
        </div>

        {deckungen.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Deckungen</span>
            {deckungen.map((d, i) => (
              <div key={d.rule_key} className="grid grid-cols-4 items-center gap-2 text-xs">
                <span className="truncate">{d.label}</span>
                <select
                  className="rounded-md border border-border bg-background p-1"
                  value={d.status}
                  onChange={(e) => setDeckungen((prev) => prev.map((x, j) => (j === i ? { ...x, status: e.target.value as OfferDeckungStatus } : x)))}
                >
                  {OFFER_DECKUNG_STATUS_VALUES.map((s) => (
                    <option key={s} value={s}>{OFFER_DECKUNG_STATUS_LABELS[s]}</option>
                  ))}
                </select>
                <input
                  type="number"
                  className="rounded-md border border-border bg-background p-1"
                  placeholder="Deckungssumme"
                  value={d.deckungssumme}
                  onChange={(e) => setDeckungen((prev) => prev.map((x, j) => (j === i ? { ...x, deckungssumme: e.target.value } : x)))}
                />
                <input
                  type="text"
                  className="rounded-md border border-border bg-background p-1"
                  placeholder="Hinweis"
                  value={d.hinweis}
                  onChange={(e) => setDeckungen((prev) => prev.map((x, j) => (j === i ? { ...x, hinweis: e.target.value } : x)))}
                />
              </div>
            ))}
          </div>
        )}

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-muted-foreground">Ausschluesse (eine pro Zeile)</span>
          <textarea
            className="min-h-[72px] rounded-md border border-border bg-background p-2 text-sm"
            value={f.ausschluesse}
            onChange={(e) => setF((prev) => ({ ...prev, ausschluesse: e.target.value }))}
            data-testid="offert-norm-ausschluesse"
          />
        </label>

        {!kernwerteOk && (
          <p className="text-xs text-status-danger">Praemie pro Jahr und Versicherungssumme sind Pflicht.</p>
        )}
        {error === 'kernwerte_fehlen' && (
          <p className="text-sm text-status-danger" role="alert">Praemie pro Jahr und Versicherungssumme sind Pflicht.</p>
        )}
      </div>
    </Dialog>
  );
}
